const PENDING_PREFIX = "pending:"

/**
 * Apply not-yet-confirmed assign/remove operations on top of the server's assignments so
 * the board updates the moment the user acts, without a blocking spinner.
 *
 * An assign replaces any existing assignment for the same target (assigning again moves
 * it), matching the server's one-record-per-target rule. Optimistic records carry
 * `pending: true` so the UI can show them as saving.
 *
 * Once an assign's save has returned and the server's list has the target on the assigned
 * trade, in a write other than the one the assign replaced, the server's own record (with
 * its real id) is shown instead of the stand-in.
 * Until the save returns the stand-in stays, even if the list already shows that trade: the
 * list's record may be an older write of the same target (on Gas, moved to Electrical and
 * straight back before either save returned). Shown as saved, it could be removed with a
 * writeId the queued saves are about to replace, and the server would then keep the chip.
 * A stand-in cannot be removed, so the user waits the moment it takes to save.
 *
 * Re-assigning a target reuses its record id, so a remove matches the exact write it was
 * aimed at (id and writeId), never just the id: removing a chip and at once assigning the
 * same target again must not hide the replacement when it arrives. For the same reason
 * the record a pending remove is aimed at never counts as an assign having been saved.
 *
 * Only the newest operation on a target counts. Assign a folder to Gas, then move its chip
 * to Electrical before the first save returns: once the server's list shows Electrical,
 * the older Gas assign would otherwise look unsaved and be put back on top of it. A later
 * assign or remove of the same target supersedes an earlier one, saved or not; if the
 * later one fails and is rolled back, the earlier one counts again.
 *
 * @param {object[]} assignments - From the server
 * @param {Array<{ type: "assign", records: object[], saved?: boolean, replaces?: Object<string, string> } | { type: "remove", refs: Array<{ id: string, writeId: string | null }>, targets?: object[] }>} operations
 *   A remove's targets are the records it removes, so it can supersede earlier assigns;
 *   saved is set once an operation's save has returned; an assign's replaces is each
 *   target's write when it was made (see currentWriteKeys)
 * @returns {object[]}
 */
export function mergeOptimisticAssignments(assignments, operations) {
    const savedByTarget = new Map(assignments.map(toTargetEntry))
    const beingRemoved = new Set(operations.flatMap(toRemovedWriteKeys))
    const latestByTarget = latestOperationByTarget(operations)
    let result = assignments
    for (const operation of operations) {
        if (operation.type === "assign") {
            const outstanding = operation.records.filter(isNewestIn(operation)).filter(isOutstandingIn(operation))
            const replaced = new Set(outstanding.map(targetKey))
            result = [...result.filter((a) => !replaced.has(targetKey(a))), ...outstanding]
        } else {
            const removed = new Set(operation.refs.map(writeKey))
            result = result.filter((a) => !removed.has(writeKey(a)))
        }
    }
    return result

    // A later assign or remove of the same target supersedes this operation's record
    function isNewestIn(operation) {
        return function isNewest(record) {
            return latestByTarget.get(targetKey(record)) === operation
        }
    }

    function isOutstandingIn(operation) {
        return function isOutstanding(record) {
            return isAssignOutstanding(operation, record, savedByTarget, beingRemoved)
        }
    }
}

/**
 * The operations still needed on top of this server list. A saved operation is dropped
 * only once the list shows it: a list read before its write landed (a refresh set off by
 * an earlier save, say) would otherwise bring the old record back as a normal, removable
 * chip. It is also dropped once a later operation on every target it touches has saved,
 * since its own write landed before that one and can no longer be what the list shows.
 *
 * @param {object[]} assignments - From the server
 * @param {object[]} operations - As for mergeOptimisticAssignments
 * @returns {object[]} The same array when nothing is dropped
 */
export function withoutSettledOperations(assignments, operations) {
    const savedByTarget = new Map(assignments.map(toTargetEntry))
    const savedWrites = new Set(assignments.map(writeKey))
    const beingRemoved = new Set(operations.flatMap(toRemovedWriteKeys))
    const kept = operations.filter(isUnsettled)
    return kept.length === operations.length ? operations : kept

    function isUnsettled(operation, index) {
        if (!operation.saved) return true
        const savedLater = new Set(
            operations
                .slice(index + 1)
                .filter(isSaved)
                .flatMap(operationTargetKeys)
        )
        if (operation.type === "assign") return operation.records.some(isStillNeeded)
        // A remove is shown once its write is gone, or once every target has a later save
        const stillListed = operation.refs.some(isListed)
        const targets = operationTargetKeys(operation)
        return stillListed && !(targets.length && targets.every(isSavedLater))

        function isStillNeeded(record) {
            return (
                !savedLater.has(targetKey(record)) &&
                isAssignOutstanding(operation, record, savedByTarget, beingRemoved)
            )
        }

        function isSavedLater(key) {
            return savedLater.has(key)
        }
    }

    function isListed(ref) {
        return savedWrites.has(writeKey(ref))
    }
}

/**
 * The write each target has on the server now, by target key. An assign keeps these so
 * that, once saved, a list still showing that same write is known to predate it.
 *
 * @param {object[]} assignments - From the server
 * @param {object[]} targets
 * @returns {Object<string, string>}
 */
export function currentWriteKeys(assignments, targets) {
    const byTarget = new Map(assignments.map(toTargetEntry))
    const writes = {}
    for (const target of targets) {
        const current = byTarget.get(targetKey(target))
        if (current) writes[targetKey(target)] = writeKey(current)
    }
    return writes
}

// Before its save returns, the server's record for the target may predate this assign.
// After it, the record is this assign's write only if it is on the assigned trade, is not
// the write the assign replaced (a list read before the save landed still has that one),
// and is not being removed (which covers callers that did not record what they replaced).
function isAssignOutstanding(operation, record, savedByTarget, beingRemoved) {
    if (!operation.saved) return true
    const key = targetKey(record)
    const saved = savedByTarget.get(key)
    return (
        !saved ||
        saved.tradeId !== record.tradeId ||
        beingRemoved.has(writeKey(saved)) ||
        operation.replaces?.[key] === writeKey(saved)
    )
}

function isSaved(operation) {
    return !!operation.saved
}

/**
 * The targets an assign or remove operation touches, as "kind:targetId" keys.
 * @param {{ type: "assign", records: object[] } | { type: "remove", targets?: object[] }} operation
 * @returns {string[]}
 */
export function operationTargetKeys(operation) {
    const targets = operation.type === "assign" ? operation.records : operation.targets ?? []
    return targets.map(targetKey)
}

function latestOperationByTarget(operations) {
    const latest = new Map()
    for (const operation of operations) {
        for (const key of operationTargetKeys(operation)) latest.set(key, operation)
    }
    return latest
}

function toRemovedWriteKeys(operation) {
    return operation.type === "remove" ? operation.refs.map(writeKey) : []
}

// One saved write of a record: the id alone is shared by every re-assign of a target
function writeKey(assignment) {
    return `${assignment.id}@${assignment.writeId ?? ""}`
}

/**
 * Stand-ins have made-up ids the server has never seen, so they must never be sent to it.
 * @param {string} id
 */
export function isPendingAssignmentId(id) {
    return String(id).startsWith(PENDING_PREFIX)
}

/**
 * The stand-in record shown until the server's own record arrives.
 */
export function pendingAssignment(target, tradeId) {
    return { ...target, id: `${PENDING_PREFIX}${targetKey(target)}`, tradeId, pending: true }
}

function toTargetEntry(assignment) {
    return [targetKey(assignment), assignment]
}

function targetKey(assignment) {
    return `${assignment.kind}:${assignment.targetId}`
}
